import React, { useState } from 'react';
import { 
  BarChart, Bar, LineChart, Line, 
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';

// Import sample data
import { countries, regions } from '../../data/geoData';
import { monthlySalesByRegion } from '../../data/timeData';
import { productCategoriesL1 } from '../../data/productData';

const RegionalComparison = () => {
  const [selectedRegions, setSelectedRegions] = useState(['North America', 'Europe', 'Asia']);
  const [comparisonMetric, setComparisonMetric] = useState('totalSales');
  const [timeRange, setTimeRange] = useState('12m');
  const [chartType, setChartType] = useState('bar');
  
  // Toggle region selection
  const toggleRegion = (regionName) => {
    if (selectedRegions.includes(regionName)) {
      setSelectedRegions(selectedRegions.filter(r => r !== regionName));
    } else {
      setSelectedRegions([...selectedRegions, regionName]);
    }
  };
  
  // Get filtered data for time series
  const getTimeSeriesData = () => {
    let filteredData = monthlySalesByRegion;
    
    // Apply time range filter
    switch (timeRange) {
      case '3m':
        filteredData = filteredData.slice(0, 3);
        break;
      case '6m':
        filteredData = filteredData.slice(0, 6);
        break;
      case '12m':
      default:
        // Use all available data
        break;
    }
    
    return filteredData;
  };
  
  // Prepare data for regional comparison chart
  const getRegionComparisonData = () => {
    const metrics = {
      totalSales: { label: 'Total Sales (USD)', format: value => `$${(value/1000000).toFixed(2)}M` },
      growth: { label: 'Growth Rate (%)', format: value => `${value.toFixed(1)}%` },
      shopCount: { label: 'Shop Count', format: value => value },
      countryCount: { label: 'Country Count', format: value => value }
    };
    
    return regions
      .filter(region => selectedRegions.includes(region.name))
      .map(region => ({
        name: region.name,
        value: region[comparisonMetric]
      }));
  };
  
  // Get regions with countries for the tree structure
  const getRegionsWithCountries = () => {
    return regions.map(region => {
      const countriesInRegion = countries.filter(country => country.region === region.name);
      return {
        ...region,
        countries: countriesInRegion
      };
    });
  };
  
  // Format metric values
  const formatMetricValue = (value) => {
    switch(comparisonMetric) {
      case 'totalSales':
        return `$${(value/1000000).toFixed(2)}M`;
      case 'growth':
        return `${value.toFixed(1)}%`;
      default:
        return value;
    }
  };
  
  // Colors for charts
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#8DD1E1'];
  
  return (
    <div className="bg-white p-6 rounded-lg shadow-sm">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-4">Regional Comparison</h1>
        <p className="text-gray-600 mb-4">
          Compare performance metrics across different geographic regions. Analyze regional trends and identify growth opportunities.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-3">Comparison Metric</label>
            <select 
              value={comparisonMetric}
              onChange={(e) => setComparisonMetric(e.target.value)}
              className="dropdown-selector"
            >
              <option value="totalSales">Total Sales (USD)</option>
              <option value="growth">Growth Rate (%)</option>
              <option value="shopCount">Shop Count</option>
              <option value="countryCount">Country Count</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-3">Time Range</label>
            <select 
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="dropdown-selector"
            >
              <option value="3m">Last 3 Months</option>
              <option value="6m">Last 6 Months</option>
              <option value="12m">Last 12 Months</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-3">Chart Type</label>
            <select 
              value={chartType}
              onChange={(e) => setChartType(e.target.value)}
              className="dropdown-selector"
            >
              <option value="bar">Bar Chart</option>
              <option value="line">Line Chart</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-3">Select Regions</label>
            <div className="flex flex-wrap gap-2">
              {regions.map(region => (
                <button
                  key={region.id}
                  onClick={() => toggleRegion(region.name)}
                  className={`px-3 py-1 text-xs font-medium rounded-full ${
                    selectedRegions.includes(region.name)
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {region.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      {/* Region Comparison Chart */}
      <div className="chart-container mb-6">
        <h2 className="chart-title">Region Comparison - {comparisonMetric === 'totalSales' ? 'Total Sales' : 
                                        comparisonMetric === 'growth' ? 'Growth Rate' : 
                                        comparisonMetric === 'shopCount' ? 'Shop Count' : 'Country Count'}</h2>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={getRegionComparisonData()}
              margin={{ top: 5, right: 30, left: 30, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip formatter={(value) => formatMetricValue(value)} />
              <Legend />
              <Bar dataKey="value" fill="#0088FE" name={comparisonMetric === 'totalSales' ? 'Total Sales' : 
                                                       comparisonMetric === 'growth' ? 'Growth Rate' : 
                                                       comparisonMetric === 'shopCount' ? 'Shop Count' : 'Country Count'} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      
      {/* Time Series Chart */}
      <div className="chart-container mb-6">
        <h2 className="chart-title">Regional Sales Trends Over Time</h2>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            {chartType === 'line' ? (
              <LineChart
                data={getTimeSeriesData()}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={(value) => `$${(value/1000000).toFixed(2)}M`} />
                <Legend />
                {selectedRegions.map((region, index) => (
                  <Line 
                    key={region}
                    type="monotone"
                    dataKey={region}
                    stroke={COLORS[index % COLORS.length]}
                    activeDot={{ r: 8 }}
                  />
                ))}
              </LineChart>
            ) : (
              <BarChart
                data={getTimeSeriesData()}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={(value) => `$${(value/1000000).toFixed(2)}M`} />
                <Legend />
                {selectedRegions.map((region, index) => (
                  <Bar 
                    key={region}
                    dataKey={region}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </BarChart>
            )}
          </ResponsiveContainer>
        </div>
      </div>
      
      {/* Region Details */}
      {getRegionsWithCountries()
        .filter(region => selectedRegions.includes(region.name))
        .map(region => (
          <div key={region.id} className="mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-3">{region.name} - Countries</h2>
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Country</th>
                    <th>Total Sales</th>
                    <th>Growth</th>
                    <th>Shop Count</th>
                    <th>Area (km²)</th>
                  </tr>
                </thead>
                <tbody>
                  {region.countries
                    .sort((a, b) => b.totalSales - a.totalSales)
                    .map(country => (
                      <tr key={country.id}>
                        <td>{country.name}</td>
                        <td>${(country.totalSales / 1000000).toFixed(2)}M</td>
                        <td className={country.growth >= 0 ? 'positive-growth' : 'negative-growth'}>
                          {country.growth >= 0 ? '↑' : '↓'} {Math.abs(country.growth).toFixed(1)}%
                        </td>
                        <td>{country.shopCount}</td>
                        <td>{country.area_km2?.toLocaleString()}</td>
                      </tr>
                    ))
                  }
                </tbody>
              </table>
            </div>
          </div>
        ))
      }
    </div>
  );
};

export default RegionalComparison;